import Pagination from '@berlitz/pagination'
import Spacer from '@berlitz/spacer'
import Table, { TableLoader } from '@components/Table'
import { ColumnProps } from '@components/Table/interface'
import WithLessonUtilsHOC, { WithLessonUtilsProp } from '@hocs/WithLessonUtils'
import { LessonHistory } from '@interfaces/LessonHistory'
import { Material as IMaterial } from '@interfaces/Material'
import paginate from '@utils/helpers/paginate'
import find from 'lodash/find'
import get from 'lodash/get'
import * as React from 'react'
import { FormattedMessage, injectIntl, WrappedComponentProps } from 'react-intl'
import { Col, Row } from 'react-styled-flexboxgrid'
import { Section } from '../Layout/style'
import { RecordTableProps } from './interface'
import {
  Box,
  ButtonContainer,
  Label,
  List,
  OutlineButton,
  PaginationContainer,
  SelectLevel,
  StyledH2,
  StyledSelect,
} from './style'

interface TableListProp {
  columns: Array<ColumnProps<LessonHistory>>
  lessonHistory: LessonHistory[]
}

const TableList: React.FunctionComponent<TableListProp> = ({ columns, lessonHistory }) => {
  const displayItem = 10
  const numberPages = Math.ceil(lessonHistory.length / displayItem)
  const [activePage, setActivePage] = React.useState(1)
  const [data, setData] = React.useState(paginate(lessonHistory, displayItem, activePage))

  React.useEffect(() => {
    setData(paginate(lessonHistory, displayItem, activePage))
  }, [lessonHistory, displayItem, activePage])

  const setPaginatedData = (page: number, passedHistory?: LessonHistory[]) => {
    const lh = passedHistory || lessonHistory
    setActivePage(page)
    setData(paginate(lh, displayItem, page))
  }

  return (
    <>
      <Table columns={columns} dataSource={data} />
      {lessonHistory.length > displayItem && (
        <PaginationContainer>
          <Pagination
            activePage={activePage}
            numberPages={numberPages}
            onChange={(pageNumber) => {
              setPaginatedData(pageNumber)
            }}
            prevLabel="Back"
            nextLabel="Next"
          />
        </PaginationContainer>
      )}
    </>
  )
}

// any changes here will also affect the `/settings/lesson-history/` page
export const MaterialRenderer: React.FunctionComponent<{ materials: IMaterial[]; record: LessonHistory }> = ({
  materials,
  record,
}) => {
  if (!materials[0]?.MaterialName && !record?.studentNote) {
    return '-'
  }

  if (!materials[0]?.MaterialName && record?.studentNote) {
    return (
      <List showBullet={false} status={record.attendance}>
        <li>
          <div>
            <Label status={record.attendance}>Own Material</Label>
            <Spacer size="xs" />
          </div>
        </li>
      </List>
    )
  }

  return (
    <>
      {materials.map(
        ({ MaterialName }, index) =>
          MaterialName && (
            <List key={`${MaterialName}-${index}`} showBullet={materials.length > 1} status={record.attendance}>
              <li>
                <div>
                  <Label status={record.attendance}>{MaterialName}</Label>
                  <Spacer size="xs" />
                </div>
              </li>
            </List>
          )
      )}
    </>
  )
}

// any changes here will affect the `/settings/lesson-history/` page
export const TopicRenderer: React.FunctionComponent<{ materials: IMaterial[]; record: LessonHistory }> = ({
  materials,
  record,
}) => {
  if (!materials[0]?.MaterialName && !materials[0]?.ChapterName) {
    return '-'
  }

  if (record?.studentNote) {
    return '-'
  }

  return (
    <>
      {materials[0]?.ChapterName &&
        materials.map(
          ({ ChapterName }, index) =>
            ChapterName && (
              <List key={`${ChapterName}-${index}`} showBullet={materials.length > 1} status={record.attendance}>
                <li>
                  <div>
                    <Label status={record.attendance}>{ChapterName}</Label>
                    <Spacer size="xs" />
                  </div>
                </li>
              </List>
            )
        )}
    </>
  )
}

const BoD1Table: React.FunctionComponent<WrappedComponentProps & RecordTableProps & WithLessonUtilsProp> = ({
  intl,
  lessonHistory = [],
  setLessonHistory,
  instructor,
  selectedStudent,
  options,
  setStudent,
  students,
  loading,
  displayLessonsBtn,
  title = 'Pedagogical record',
}) => {
  const [showMyLessons, setShowMyLessons] = React.useState(false)
  let defaultColumns = [
    {
      title: intl.formatMessage({ id: 'Date' }),
      dataIndex: 'date',
      key: 'date',
      align: 'left',
      width: '15%',
      // eslint-disable-next-line react/display-name
      render: (text: string, record: LessonHistory) => <Label status={record.attendance}>{text}</Label>,
    },
    {
      title: intl.formatMessage({ id: 'Material' }),
      dataIndex: 'materials',
      key: 'materials',
      align: 'left',
      // eslint-disable-next-line react/display-name
      render: (materials: IMaterial[], record: LessonHistory) => (
        <>
          <MaterialRenderer materials={materials} record={record} />
        </>
      ),
    },
    {
      title: intl.formatMessage({ id: 'Topic' }),
      dataIndex: 'materials',
      key: 'materials',
      align: 'left',
      width: '20%',
      render: (materials: IMaterial[], record: LessonHistory) => (
        <>
          <TopicRenderer materials={materials} record={record} />
        </>
      ),
    },
    {
      title: intl.formatMessage({ id: 'Custom Lesson' }),
      dataIndex: 'studentNote',
      key: 'studentNote',
      align: 'left',
      render: (studentNote: string, _record: LessonHistory) => (
        <>
          {!studentNote && '-'}
          {studentNote && <Label>{studentNote}</Label>}
        </>
      ),
    },
    {
      title: intl.formatMessage({ id: 'Instructor' }),
      dataIndex: 'instructor',
      key: 'instructor',
      align: 'left',
      // eslint-disable-next-line react/display-name
      render: (text: string, record: LessonHistory) => <Label status={record.attendance}>{text}</Label>,
    },
  ]

  return (
    <Section>
      <Box>
        <Row>
          <Col xs={12} md={8}>
            <StyledH2>{intl.formatMessage({ id: title })}</StyledH2>
          </Col>
          <Col xs={12} md={4}>
            <ButtonContainer>
              {displayLessonsBtn && (
                <OutlineButton
                  outline={!showMyLessons}
                  onClick={() => {
                    const isShown = !showMyLessons
                    setShowMyLessons(isShown)

                    if (isShown) {
                      const name = [
                        get(instructor, 'InstructorProfile.instructorInfo.contactInfo.FirstName'),
                        get(instructor, 'InstructorProfile.instructorInfo.contactInfo.LastName'),
                      ].join(' ')

                      const lessons = lessonHistory.filter((lesson) => lesson.instructor === name)
                      setLessonHistory(lessons)
                    } else {
                      setLessonHistory()
                    }
                  }}
                >
                  <FormattedMessage id="My lessons" />
                </OutlineButton>
              )}
            </ButtonContainer>
          </Col>
          {options.length > 1 && (
            <Col xs={12} md={2}>
              <SelectLevel>
                <StyledSelect
                  label=""
                  name="Student"
                  value={get(selectedStudent, 'CustomerId')}
                  onChange={(value) => {
                    const student = find(students, { CustomerId: value })
                    if (student) {
                      setStudent(student)
                    }
                  }}
                  options={options}
                  disableMargin
                />
              </SelectLevel>
            </Col>
          )}
        </Row>
        {loading && <TableLoader />}
        {!loading && <TableList columns={defaultColumns} lessonHistory={lessonHistory} />}
      </Box>
    </Section>
  )
}

export default WithLessonUtilsHOC(injectIntl(BoD1Table))
